import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import SEOHead from './SEOHead';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  const [step, setStep] = useState('loading'); // loading, security, newPassword, success, error
  const [securityQuestion, setSecurityQuestion] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Fetch security question for the token
    const fetchSecurityQuestion = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/reset-password/${token}`);
        const data = await response.json();
        
        if (!response.ok) {
          setError(data.detail || 'Invalid or expired reset link');
          setStep('error');
          return;
        }
        
        setSecurityQuestion(data.security_question);
        setEmail(data.email);
        setStep('security');
      } catch (err) {
        setError('Failed to verify reset link. Please try again.');
        setStep('error');
      }
    };
    
    if (token) {
      fetchSecurityQuestion();
    } else {
      setError('No reset token provided');
      setStep('error');
    }
  }, [token, API_URL]);

  const handleSecuritySubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!securityAnswer.trim()) {
      setError('Please enter your security answer');
      return;
    }
    
    // Move to password step - we'll verify the answer when submitting the new password
    setStep('newPassword');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          security_answer: securityAnswer,
          new_password: newPassword
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setError(data.detail || 'Failed to reset password');
        if (data.detail?.includes('security answer')) {
          setStep('security');
        }
        setLoading(false);
        return;
      }
      
      setStep('success');
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderLoading = () => (
    <div className="text-center py-8">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-gray-400">Verifying reset link...</p>
    </div>
  );

  const renderError = () => (
    <div className="text-center py-8">
      <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">Reset Link Invalid</h3>
      <p className="text-gray-400 mb-6">{error}</p>
      <Link
        to="/support"
        className="inline-block px-6 py-2 bg-primary hover:bg-primary/90 text-black font-semibold rounded-lg transition-colors"
      >
        Back to Login
      </Link>
    </div>
  );

  const renderSecurity = () => (
    <form onSubmit={handleSecuritySubmit} className="space-y-6">
      <div className="text-center mb-6">
        <p className="text-gray-400">
          To reset your password for <span className="text-white font-medium">{email}</span>, 
          please answer your security question.
        </p>
      </div>
      
      <div className="bg-slate-800/50 p-4 rounded-lg border border-white/10">
        <label className="block text-sm font-medium text-gray-400 mb-2">Security Question</label>
        <p className="text-white font-medium">{securityQuestion}</p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Your Answer</label>
        <input
          type="text"
          value={securityAnswer}
          onChange={(e) => setSecurityAnswer(e.target.value)}
          required
          placeholder="Enter your answer"
          className="w-full px-4 py-3 bg-slate-800 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid="security-answer-input"
        />
      </div>
      
      <button
        type="submit"
        className="w-full py-3 bg-primary hover:bg-primary/90 text-black font-semibold rounded-lg transition-colors"
        data-testid="verify-answer-btn"
      >
        Verify Answer
      </button>
      
      <div className="text-center">
        <Link
          to="/support"
          className="text-gray-400 hover:text-white text-sm"
        >
          ← Back to Sign In
        </Link>
      </div>
    </form>
  );

  const renderNewPassword = () => (
    <form onSubmit={handlePasswordSubmit} className="space-y-6">
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
          <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
          </svg>
        </div>
        <p className="text-gray-400">
          Great! Now create a new password for your account.
        </p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">New Password</label>
        <input
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          minLength={6}
          placeholder="Enter new password"
          className="w-full px-4 py-3 bg-slate-800 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid="new-password-input"
        />
        <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Confirm New Password</label>
        <input
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          minLength={6}
          placeholder="Confirm new password"
          className="w-full px-4 py-3 bg-slate-800 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid="confirm-password-input"
        />
      </div>
      
      <button
        type="submit"
        disabled={loading}
        className="w-full py-3 bg-primary hover:bg-primary/90 text-black font-semibold rounded-lg transition-colors disabled:opacity-50"
        data-testid="reset-password-btn"
      >
        {loading ? 'Resetting Password...' : 'Reset Password'}
      </button>
      
      <div className="text-center">
        <button
          type="button"
          onClick={() => { setStep('security'); setError(''); }}
          className="text-gray-400 hover:text-white text-sm"
        >
          ← Back to Security Question
        </button>
      </div>
    </form>
  );

  const renderSuccess = () => (
    <div className="text-center py-8">
      <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
        </svg>
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">Password Reset Successful!</h3>
      <p className="text-gray-400 mb-6">
        Your password has been updated. You can now sign in with your new password.
      </p>
      <Link
        to="/support"
        className="inline-block px-6 py-3 bg-primary hover:bg-primary/90 text-black font-semibold rounded-lg transition-colors"
        data-testid="back-to-login-btn"
      >
        Sign In Now
      </Link>
    </div>
  );

  return (
    <>
      <SEOHead
        title="Reset Password - King of IT Support Portal"
        description="Reset your King of IT support portal password. Secure password recovery with security question verification."
        path="/support/reset-password"
        noindex={true}
      />
      
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">
              King of IT Support
            </h1>
            <p className="text-gray-400">
              {step === 'loading' && 'Verifying...'}
              {step === 'security' && 'Verify Your Identity'}
              {step === 'newPassword' && 'Create New Password'}
              {step === 'success' && 'All Done!'}
              {step === 'error' && 'Something Went Wrong'}
            </p>
          </div>
          
          <div className="bg-white/5 backdrop-blur-lg rounded-xl p-8 border border-white/10">
            {error && step !== 'error' && (
              <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-6">
                {error}
              </div>
            )}
            
            {step === 'loading' && renderLoading()}
            {step === 'error' && renderError()}
            {step === 'security' && renderSecurity()}
            {step === 'newPassword' && renderNewPassword()}
            {step === 'success' && renderSuccess()}
          </div>
          
          <div className="mt-6 text-center">
            <a href="/" className="text-gray-400 hover:text-white transition-colors">
              ← Back to Website
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
